(() => {
  let nextId = 0;
  class LpoInfo extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.product-details');
      if (!this.root || typeof HTMLElement.prototype.showPopover !== 'function') return;
      this.records = new Map();
      this.abort = new AbortController();
      window.addEventListener('options:rendered', () => this.schedule(), { signal: this.abort.signal });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.root, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['data-rughaus-info', 'data-info-title', 'data-info-details'] });
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.resizeObserver.observe(this.root);
      this.update();
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    }
    registrationFor(help) {
      const marker = help?.querySelector(':scope > [data-rughaus-info][data-info-title]');
      const heading = marker?.dataset.infoTitle.trim();
      if (!marker || !heading || !marker.textContent.trim() || marker.querySelector('input, select, textarea, form')) return null;
      if ([...help.childNodes].some(node => node !== marker && (node.nodeType === 1 || (node.nodeType === 3 && node.textContent.trim())))) return null;
      return { marker, heading };
    }
    setHeading(record, heading) {
      record.trigger.setAttribute('aria-label', heading);
      record.panel.setAttribute('aria-label', heading);
      const title = record.anchor.querySelector('strong');
      if (title.textContent !== heading) title.textContent = heading;
    }
    syncDetails(record) {
      const replacement = record.marker.querySelector(':scope > details[data-info-details]');
      let detail = record.detail;
      if (detail && (!record.marker.contains(detail.button) || !record.option.contains(detail.dialog) || !detail.dialog.contains(detail.source) || detail.source.querySelector(':scope > summary') !== detail.summary || !detail.summary.textContent.trim() || !detail.source.hasAttribute('data-info-details') || !detail.source.dataset.infoTitle?.trim() || replacement)) {
        this.removeDetails(record, true);
        detail = null;
      }
      if (detail) {
        const heading = detail.source.dataset.infoTitle.trim();
        detail.dialog.setAttribute('aria-label', heading);
        if (detail.title.textContent !== heading) detail.title.textContent = heading;
        if (detail.button.textContent !== detail.summary.textContent) detail.button.textContent = detail.summary.textContent;
        return;
      }
      const sources = record.marker.querySelectorAll(':scope > details[data-info-details][data-info-title]');
      const source = sources.length === 1 ? sources[0] : null;
      const summary = source?.querySelector(':scope > summary');
      const heading = source?.dataset.infoTitle?.trim();
      if (!source || !summary?.textContent.trim() || !heading || typeof HTMLDialogElement.prototype.showModal !== 'function') return;
      if (![...source.children].some(child => child !== summary && child.textContent.trim())) return;
      const dialog = this.querySelector('[data-lpo-info-detail-template]').content.firstElementChild.cloneNode(true);
      const title = dialog.querySelector('[data-lpo-info-detail-title]');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'jp-lpo-info__detail-link';
      button.textContent = summary.textContent;
      const id = `LpoInfoDetail-${++nextId}`;
      dialog.id = id;
      dialog.setAttribute('aria-label', heading);
      title.textContent = heading;
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-controls', id);
      const controller = new AbortController();
      record.detail = { source, summary, wasOpen: source.open, dialog, button, title, controller };
      source.replaceWith(button);
      source.open = true;
      dialog.querySelector('[data-lpo-info-detail-copy]').append(source);
      record.option.append(dialog);
      const signal = controller.signal;
      button.addEventListener('click', () => {
        clearTimeout(record.hoverTimer);
        if (record.panel.matches(':popover-open')) record.panel.hidePopover();
        dialog.showModal();
        dialog.scrollTop = 0;
      }, { signal });
      dialog.querySelector('[data-lpo-info-detail-close]').addEventListener('click', () => dialog.close(), { signal });
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); }, { signal });
      dialog.addEventListener('close', () => { if (record.trigger.isConnected) record.trigger.focus({ preventScroll: true }); }, { signal });
    }
    removeDetails(record, restore) {
      const detail = record.detail;
      if (!detail) return;
      detail.controller.abort();
      if (detail.dialog.open) detail.dialog.close();
      if (restore && detail.button.parentNode) {
        detail.source.open = detail.wasOpen;
        detail.button.replaceWith(detail.source);
      } else detail.button.remove();
      detail.dialog.remove();
      record.detail = null;
    }
    update() {
      for (const [option, record] of this.records) {
        const replacement = option.querySelector(':scope > .cl-po--help');
        if (!this.root.contains(option) || !record.label.contains(record.anchor) || !record.anchor.contains(record.help) || replacement) {
          this.removeRecord(record, !this.root.contains(option) && !replacement);
          this.records.delete(option);
        } else {
          const registration = this.registrationFor(record.help);
          if (!registration || registration.marker !== record.marker || record.detail?.source.querySelector('input, select, textarea, form')) {
            this.removeRecord(record, true);
            this.records.delete(option);
          } else {
            this.setHeading(record, registration.heading);
            this.syncDetails(record);
          }
        }
      }
      for (const option of this.root.querySelectorAll('.cl-po--option')) {
        if (this.records.has(option)) continue;
        const label = option.querySelector(':scope > .cl-po--label');
        const help = option.querySelector(':scope > .cl-po--help');
        const registration = this.registrationFor(help);
        if (!label || !registration) continue;
        const anchor = this.querySelector('[data-lpo-info-template]').content.firstElementChild.cloneNode(true);
        const trigger = anchor.querySelector('[ref="trigger"]');
        const panel = anchor.querySelector('[popover]');
        const id = `LpoInfo-${++nextId}`;
        panel.id = id;
        trigger.setAttribute('popovertarget', id);
        trigger.setAttribute('aria-controls', id);
        trigger.setAttribute('aria-expanded', 'false');
        const close = anchor.querySelector('[data-lpo-info-close]');
        close.setAttribute('popovertarget', id);
        close.setAttribute('popovertargetaction', 'hide');
        const controller = new AbortController();
        const signal = controller.signal;
        const labelHadClass = label.classList.contains('jp-lpo-info__label');
        const record = { option, label, help, helpNextSibling: help.nextSibling, anchor, controller, trigger, panel, marker: registration.marker, labelHadClass };
        this.setHeading(record, registration.heading);
        const hide = () => { if (panel.matches(':popover-open')) panel.hidePopover(); };
        const position = () => {
          if (record.positionFrame) return;
          record.positionFrame = requestAnimationFrame(() => {
            record.positionFrame = null;
            if (!panel.isConnected || !panel.matches(':popover-open')) return;
            const a = trigger.getBoundingClientRect(), b = panel.getBoundingClientRect();
            panel.style.left = `${Math.max(16, Math.min(a.left, innerWidth - b.width - 16))}px`;
            const above = a.top - b.height - 8;
            panel.style.top = `${Math.max(16, above >= 16 ? above : Math.min(a.bottom + 8, innerHeight - b.height - 16))}px`;
          });
        };
        record.position = position;
        panel.addEventListener('toggle', () => {
          const open = panel.matches(':popover-open');
          trigger.setAttribute('aria-expanded', String(open));
          if (open) position();
          else clearTimeout(record.hoverTimer);
        }, { signal });
        panel.addEventListener('beforetoggle', event => {
          if (event.newState === 'open') position();
        }, { signal });
        const hover = (event, open) => {
          if (record.detail?.dialog.open || event.pointerType !== 'mouse' || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
          clearTimeout(record.hoverTimer);
          record.hoverTimer = setTimeout(() => {
            if (!panel.isConnected) return;
            if (open) {
              if (!panel.matches(':popover-open')) panel.showPopover();
            } else hide();
          }, 200);
        };
        trigger.addEventListener('pointerenter', event => hover(event, true), { signal });
        trigger.addEventListener('pointerleave', event => hover(event, false), { signal });
        panel.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') clearTimeout(record.hoverTimer);
        }, { signal });
        panel.addEventListener('pointerleave', event => hover(event, false), { signal });
        window.addEventListener('scroll', position, { signal, capture: true });
        window.addEventListener('resize', position, { signal });
        window.visualViewport?.addEventListener('resize', position, { signal });
        window.visualViewport?.addEventListener('scroll', position, { signal });
        anchor.querySelector('[data-lpo-info-copy]').append(help);
        label.classList.add('jp-lpo-info__label');
        label.append(anchor);
        this.records.set(option, record);
        this.syncDetails(record);
      }
      for (const record of this.records.values()) record.position();
    }
    removeRecord(record, restore) {
      record.controller.abort();
      clearTimeout(record.hoverTimer);
      cancelAnimationFrame(record.positionFrame);
      this.removeDetails(record, restore);
      if (restore) {
        const next = record.helpNextSibling?.parentNode === record.option ? record.helpNextSibling : null;
        record.option.insertBefore(record.help, next);
      }
      record.anchor.remove();
      if (!record.labelHadClass) record.label.classList.remove('jp-lpo-info__label');
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      for (const record of this.records?.values() || []) this.removeRecord(record, true);
      this.records?.clear();
    }
  }
  if (!customElements.get('jp-lpo-info')) customElements.define('jp-lpo-info', LpoInfo);
})();
